import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  formatMoney,
  inBasket,
  moveItemPatch,
  planEstimate,
  tripTotals,
  type Store,
  type SyncChangeData,
  type TripItem,
  type TripItemPatch,
} from '@sp/shared'
import { useSignedIn } from '../../session.tsx'
import { api } from '../../lib/api.ts'
import { HttpError } from '../../lib/http.ts'
import { useDesktop } from '../../lib/useDesktop.ts'
import { useCatalog, useFrequent, useTemplates } from '../../lib/groupData.ts'
import { errorMessage } from '../../lib/useResource.ts'
import { useWakeLock } from '../../lib/useWakeLock.ts'
import { newId } from '../../lib/storage.ts'
import { liftRow, restingRect, useFlip, type Lift } from '../../lib/springDrag.ts'
import {
  estimateText,
  formatCzkRounded,
  formatQty,
  intoStore,
  itemsCount,
  joinNames,
  storeShortName,
  tripName,
  unitPrice,
} from '../../lib/format.ts'
import { Icon } from '../../components/Icon.tsx'
import { Bump } from '../../components/Bump.tsx'
import {
  AvatarStack,
  Button,
  Chip,
  Divider,
  EmptyState,
  Footer,
  IconButton,
  Label,
  Paragraph,
  ScreenHeader,
  SectionHead,
  SelectRow,
  Stepper,
  TextCol,
} from '../../components/ui.tsx'
import { SyncStatus } from '../../components/bits.tsx'
import { DeskToolbar, Inspector, MoneyLine, Panel } from '../../components/desk.tsx'
import { DealTag } from '../../components/OfferBadge.tsx'
import { ItemSheet } from '../../components/ItemSheet.tsx'
import { RouteEditor } from '../../components/RouteEditor.tsx'
import { TripMenuSheet } from '../../components/TripMenuSheet.tsx'
import { useTripDeals } from '../../components/DealsSheet.tsx'
import { useUndoToast } from '../../components/UndoToast.tsx'
import { useContextMenu, type ContextAction } from '../../components/ContextMenu.tsx'
import { ConfirmSheet } from '../../components/ConfirmSheet.tsx'
import { BarcodeScanner } from '../../components/BarcodeScanner.tsx'
import { ScanLinkSheet } from './ScanLinkSheet.tsx'
import { AddBar, type NewTripItem } from './AddBar.tsx'
import { ShopSectionBody, remainingText, shopSectionMeta, shopTotals } from './ShopSection.tsx'
import {
  otherStores,
  ratesFor,
  routeOf,
  sectionsFor,
  storeOfItem,
  type Section,
  type TripViewProps,
} from './shared.ts'
import { ScrollRow } from '../../components/ScrollRow.tsx'

interface DragState {
  itemId: string
  /** Sekce, odkud se tahne — do „Ostatní" jde pustit jen polozku, ktera tam uz je. */
  origin: string
  sectionKey: string
  /** Pozice mezi ostatnimi polozkami cilove sekce (bez tazene). */
  index: number
}

/** Jak blizko okraje obrazovky se pri tazeni zacne stranka posouvat. */
const SCROLL_EDGE = 72
/** Spodni okraj je vys — nad patickou s odhadem a tlacitkem. */
const SCROLL_EDGE_BOTTOM = 150
/** Jak dlouho zustava otazka „Kam?" po pridani neznamych veci. */
const PLACE_MS = 12_000

/** Behem tahu se radek ukazuje tam, kam po pusteni spadne. */
function previewSections(sections: Section[], drag: DragState): Section[] {
  const item = sections.flatMap((s) => s.items).find((i) => i.id === drag.itemId)
  if (!item) return sections
  return sections.map((section) => {
    const rest = section.items.filter((i) => i.id !== drag.itemId)
    if (section.key !== drag.sectionKey) return { ...section, items: rest }
    const next = [...rest]
    next.splice(Math.min(drag.index, next.length), 0, item)
    return { ...section, items: next }
  })
}

/** Polozka znovu na nakup po „Vrátit" — nove id, stejna data (smazane id server nevrati). */
function readdChange(item: TripItem): SyncChangeData {
  return {
    op: 'add',
    changedAt: new Date().toISOString(),
    item: {
      id: newId(),
      catalogItemId: item.catalogItemId,
      name: item.name,
      storeId: item.storeId,
      qty: item.qty,
      unit: item.unit,
      expectedPriceMinor: item.expectedPriceMinor,
      expectedCurrency: item.expectedCurrency,
      note: item.note,
      source: item.source,
      deal: item.deal,
    },
  }
}

/**
 * Jeden nakup, jedna obrazovka (navrh v3): drive Planovani, V obchode a Bezny
 * nakup. Stav „Plánuje se" ma mnozstvi, presouvani a odhad; „Nakupuje se" ma
 * kolecka, soucet v obchode a ctecku. Pole na pridani je v obou. Sekce obchodu
 * jsou videt jen s vic obchody. Na pocitaci nastenka se sloupcem na obchod.
 */
export function TripView({ trip, sync, stores, fx, forceShop }: TripViewProps & { forceShop: boolean }) {
  const s = useSignedIn()
  const navigate = useNavigate()
  const desktop = useDesktop()
  // Po pridani sablony (Grilovacka) do nepojmenovaneho nakupu se nabidne nazev podle ni.
  const location = useLocation()
  const suggestedTitle = (location.state as { suggestTitle?: string } | null)?.suggestTitle
  const [titleDismissed, setTitleDismissed] = useState(false)
  const catalog = useCatalog(trip.groupId)
  const templates = useTemplates(trip.groupId)
  const frequent = useFrequent(trip.groupId)
  const [itemId, setItemId] = useState<string | null>(null)
  const [menu, setMenu] = useState(false)
  const [placing, setPlacing] = useState<string[]>([])
  const [drag, setDrag] = useState<DragState | null>(null)
  const [routeDraft, setRouteDraft] = useState<string[] | null>(null)
  const [routeBusy, setRouteBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  /** Panel polozky otevreny naskenovanim — rovnou s cenou. */
  const [scanPrice, setScanPrice] = useState(false)
  const [unknownCode, setUnknownCode] = useState<string | null>(null)
  const [confirmFinish, setConfirmFinish] = useState(false)
  const { showUndo, undoToast } = useUndoToast()
  const { onContextMenu, contextMenu } = useContextMenu()

  const done = trip.status === 'done'
  const mode: 'plan' | 'shop' = trip.status === 'planning' && !forceShop ? 'plan' : 'shop'
  useWakeLock(mode === 'shop' && !done)

  const listRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const pointerX = useRef(0)
  const pointerY = useRef(0)
  const dragOnDesktop = useRef(false)
  const frame = useRef<number | null>(null)
  const stopDrag = useRef<(() => void) | null>(null)
  /** Drzena kopie radku (Pruzina); null s omezenym pohybem v systemu. */
  const lift = useRef<Lift | null>(null)
  const captureRows = useFlip(listRef, '[data-item-id]', 'data-item-id')
  // Posluchace na window vznikaji pri zacatku tahu; data ctou odsud, ne z uzaveru.
  const latest = useRef({ trip, stores })
  latest.current = { trip, stores }

  useEffect(
    () => () => {
      stopDrag.current?.()
      lift.current?.destroy()
    },
    [],
  )

  useEffect(() => {
    if (placing.length === 0) return
    const timer = setTimeout(() => setPlacing([]), PLACE_MS)
    return () => clearTimeout(timer)
  }, [placing])

  const route = routeOf(trip, stores)
  const baseSections = sectionsFor(trip, stores)
  const sections = drag ? previewSections(baseSections, drag) : baseSections
  const withHeads = sections.length > 1
  // Hlavicka obchodu ukazuje stav bez rozpracovaneho tahu — pocet poskoci az po pusteni.
  const baseByKey = new Map(baseSections.map((section) => [section.key, section]))
  const rates = ratesFor(trip, fx)
  const currencies = route.map((store) => store.currency)
  const estimate = planEstimate(trip.items, rates)
  const totals = tripTotals(trip.items, rates)
  const item = trip.items.find((i) => i.id === itemId) ?? null
  const catalogItems = catalog.res.state === 'ok' ? catalog.res.data : []
  const remaining = trip.items.filter((i) => i.status === 'pending').length
  const { dealsSheet, dealsBanner, flashClass, dealLabel, itemDeals } = useTripDeals({
    trip,
    stores,
    sync,
    rates,
    catalog: catalogItems,
    onCatalogSaved: (updated) => catalog.setData((prev) => prev.map((c) => (c.id === updated.id ? updated : c))),
  })
  const name = tripName(trip, stores)

  function removeItem(target: TripItem) {
    sync.remove(target.id)
    showUndo(`${target.name}: odebráno`, () => sync.enqueue([readdChange(target)]))
  }

  function setQty(target: TripItem, qty: number) {
    if (qty <= 0) removeItem(target)
    else sync.change(target.id, { qty })
  }

  function addItems(items: NewTripItem[], unplaced: string[]) {
    const changedAt = new Date().toISOString()
    sync.enqueue(items.map((newItem) => ({ op: 'add', item: newItem, changedAt })))
    if (unplaced.length > 0) setPlacing((prev) => [...prev, ...unplaced])
  }

  const toggleFound = (it: TripItem) =>
    sync.change(
      it.id,
      it.status === 'pending' || it.status === 'not_found'
        ? { status: 'found', actualPriceMinor: null }
        : { status: 'pending', actualPriceMinor: null },
    )

  const moveTo = (it: TripItem, target: Store) =>
    sync.change(it.id, { ...moveItemPatch(it, target), status: 'pending', actualPriceMinor: null })

  function goShopping() {
    // Stav jde pres frontu — bez signalu se nakupuje i tak a odejde pozdeji.
    if (trip.status === 'planning') sync.setStatus('shopping')
    navigate(`/nakup/${trip.id}/obchod`, { replace: true })
  }

  /** Zpet k planovani: stav nakupu se vrati (pres frontu, i bez signalu). */
  function backToPlanning() {
    sync.setStatus('planning')
    navigate(`/nakup/${trip.id}`, { replace: true })
  }

  /** Hotovo jde i bez signalu: odejde az po vsech odskrtnutich (tripSync). */
  function finishNow() {
    setConfirmFinish(false)
    sync.setStatus('done')
    navigate('/')
  }

  function finish() {
    if (done) {
      navigate('/')
      return
    }
    if (remaining > 0) setConfirmFinish(true)
    else finishNow()
  }

  async function saveToTemplate(target: TripItem) {
    const created = await api.catalog.create(trip.groupId, {
      name: target.name,
      storeId: target.storeId,
      defaultQty: target.qty,
      unit: target.unit,
      typicalPriceMinor: target.expectedPriceMinor,
      typicalCurrency: target.expectedPriceMinor === null ? null : target.expectedCurrency,
    })
    catalog.setData((prev) => (prev.some((c) => c.id === created.id) ? prev : [...prev, created]))
    sync.change(target.id, { catalogItemId: created.id })
  }

  async function saveRoute(next: string[]) {
    setRouteBusy(true)
    setError(null)
    try {
      await sync.updateTrip({ storeIds: next })
      setRouteDraft(null)
    } catch (err) {
      setError(err instanceof HttpError && err.isOffline ? 'Trasu jde změnit jen se signálem.' : errorMessage(err))
    } finally {
      setRouteBusy(false)
    }
  }

  /** Znamy kod polozky na seznamu -> cena a do kosiku. Neznamy -> prirazeni k polozce. */
  function scanned(code: string) {
    setScanning(false)
    const known = new Set(catalogItems.filter((ci) => ci.barcodes.includes(code)).map((ci) => ci.id))
    const onList = trip.items.filter((it) => it.catalogItemId !== null && known.has(it.catalogItemId))
    const target = onList.find((it) => !inBasket(it)) ?? onList[0]
    if (target) {
      setScanPrice(true)
      setItemId(target.id)
    } else {
      setUnknownCode(code)
    }
  }

  /**
   * Polozka na nove misto: jiny obchod a/nebo jine poradi. Sekce si mezi sebou
   * rozdeli pozice, ktere uz mela, takze poradi v ostatnich obchodech zustane.
   */
  function commitMove(target: { itemId: string; sectionKey: string; index: number }) {
    const { trip: current, stores: allStores } = latest.current
    const moving = current.items.find((i) => i.id === target.itemId)
    const section = sectionsFor(current, allStores).find((x) => x.key === target.sectionKey)
    if (!moving || !section) return

    const rest = section.items.filter((i) => i.id !== moving.id)
    const order = [...rest]
    order.splice(Math.min(target.index, order.length), 0, moving)
    let slots = [...rest.map((i) => i.position), moving.position].sort((a, b) => a - b)
    if (new Set(slots).size !== slots.length) {
      // Dve polozky se stejnou pozici (soubezne pridani) — precislovat za konec.
      const base = current.items.reduce((max, i) => Math.max(max, i.position), -1) + 1
      slots = order.map((_, i) => base + i)
    }

    const toStore = section.store
    const changedAt = new Date().toISOString()
    const changes: SyncChangeData[] = []
    order.forEach((it, i) => {
      const patch: TripItemPatch = {}
      const slot = slots[i]
      if (slot !== undefined && it.position !== slot) patch.position = slot
      if (it.id === moving.id && toStore && it.storeId !== toStore.id) {
        Object.assign(patch, moveItemPatch(it, toStore))
        if (it.status === 'not_found') patch.status = 'pending'
      }
      if (Object.keys(patch).length > 0) changes.push({ op: 'patch', itemId: it.id, patch, changedAt })
    })
    sync.enqueue(changes)
  }

  function relocate() {
    const state = dragRef.current
    const root = listRef.current
    if (!state || !root) return
    const x = pointerX.current
    const y = pointerY.current
    const targets = [...root.querySelectorAll<HTMLElement>('[data-section-key]')].filter((el) => {
      const key = el.dataset['sectionKey']
      return key === state.origin || route.some((store) => store.id === key)
    })
    let target = targets[0]
    if (dragOnDesktop.current) {
      // Sloupce muzou byt vedle sebe i pod sebou: cilem je ten nejblize pointeru.
      let best = Infinity
      for (const column of targets) {
        const rect = column.getBoundingClientRect()
        const dx = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0
        const dy = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0
        const distance = dx * dx + dy * dy
        if (distance < best) {
          best = distance
          target = column
        }
      }
    } else {
      for (const candidate of targets) if (candidate.getBoundingClientRect().top <= y) target = candidate
    }
    const sectionKey = target?.dataset['sectionKey']
    if (!sectionKey) return
    const rows = [...root.querySelectorAll<HTMLElement>('[data-row-section]')].filter(
      (row) => row.dataset['rowSection'] === sectionKey && row.dataset['itemId'] !== state.itemId,
    )
    const index = rows.filter((row) => {
      const rect = restingRect(row)
      return rect.top + rect.height / 2 < y
    }).length
    if (sectionKey === state.sectionKey && index === state.index) return
    dragRef.current = { ...state, sectionKey, index }
    captureRows()
    setDrag(dragRef.current)
  }

  function autoScroll() {
    frame.current = null
    if (!dragRef.current) return
    const y = pointerY.current
    const bottom = window.innerHeight - (dragOnDesktop.current ? SCROLL_EDGE : SCROLL_EDGE_BOTTOM)
    const dy = y < SCROLL_EDGE ? -Math.ceil((SCROLL_EDGE - y) / 6) : y > bottom ? Math.ceil((y - bottom) / 6) : 0
    if (dy === 0) return
    window.scrollBy(0, dy)
    relocate()
    frame.current = requestAnimationFrame(autoScroll)
  }

  function startDrag(e: PointerEvent<HTMLButtonElement>, target: TripItem, sectionKey: string, index: number) {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    e.preventDefault()
    stopDrag.current?.()
    const state: DragState = { itemId: target.id, origin: sectionKey, sectionKey, index }
    const pointerId = e.pointerId
    dragRef.current = state
    dragOnDesktop.current = desktop
    pointerX.current = e.clientX
    pointerY.current = e.clientY
    lift.current?.destroy()
    const rowEl = e.currentTarget.closest<HTMLElement>('[data-item-id]')
    lift.current = rowEl ? liftRow(rowEl, e.clientX, e.clientY, desktop ? 'board' : undefined) : null
    setDrag(state)
    document.body.classList.add('is-dragging')

    // Posluchace na window, ne na uchytu: pri presunu do jine sekce React
    // radek prestavi a puvodni element i s pointer capture zmizi.
    const onMove = (ev: globalThis.PointerEvent) => {
      if (ev.pointerId !== pointerId) return
      pointerX.current = ev.clientX
      pointerY.current = ev.clientY
      lift.current?.move(ev.clientX, ev.clientY)
      relocate()
      if (frame.current === null) frame.current = requestAnimationFrame(autoScroll)
    }
    const onEnd = (ev: globalThis.PointerEvent) => {
      if (ev.pointerId !== pointerId) return
      const final = dragRef.current
      cleanup()
      if (final && ev.type === 'pointerup') commitMove(final)
      // Kopie dosedne tam, kde radek po vykresleni skutecne je; pri zruseni zpet.
      const landed = lift.current
      lift.current = null
      landed?.drop(
        () => listRef.current?.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(target.id)}"]`) ?? null,
      )
    }
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onEnd)
      window.removeEventListener('pointercancel', onEnd)
      document.body.classList.remove('is-dragging')
      if (frame.current !== null) cancelAnimationFrame(frame.current)
      frame.current = null
      dragRef.current = null
      stopDrag.current = null
      setDrag(null)
    }
    stopDrag.current = cleanup
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onEnd)
    window.addEventListener('pointercancel', onEnd)
  }

  /** Po presunu klavesnici do jineho sloupce se radek prestavi — fokus se musi vratit na uchyt. */
  function refocusGrip(id: string) {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        listRef.current?.querySelector<HTMLElement>(`[data-item-id="${id}"] .grip`)?.focus()
      }),
    )
  }

  /**
   * Sipky nahoru a dolu: o misto vedle, na hranici sekce do sousedniho obchodu.
   * Na pocitaci sipky doleva a doprava do vedlejsiho sloupce.
   */
  function onGripKey(e: KeyboardEvent<HTMLButtonElement>, target: TripItem, sectionKey: string, index: number) {
    const horizontal = desktop && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && !horizontal) return
    e.preventDefault()
    captureRows()
    const targets = baseSections.filter((x) => x.store !== null || x.key === sectionKey)
    const at = targets.findIndex((x) => x.key === sectionKey)
    const section = targets[at]
    if (!section) return
    if (horizontal) {
      const neighbor = targets[at + (e.key === 'ArrowLeft' ? -1 : 1)]
      if (!neighbor) return
      commitMove({ itemId: target.id, sectionKey: neighbor.key, index: Math.min(index, neighbor.items.length) })
    } else if (e.key === 'ArrowUp') {
      const previous = targets[at - 1]
      if (index > 0) commitMove({ itemId: target.id, sectionKey, index: index - 1 })
      else if (previous) commitMove({ itemId: target.id, sectionKey: previous.key, index: previous.items.length })
    } else {
      const next = targets[at + 1]
      if (index < section.items.length - 1) commitMove({ itemId: target.id, sectionKey, index: index + 1 })
      else if (next) commitMove({ itemId: target.id, sectionKey: next.key, index: 0 })
    }
    refocusGrip(target.id)
  }

  /** Pocet a odhad obchodu; po presunu polozky kratce poskoci. */
  function sectionMetaBump(section: Section, className?: string) {
    const base = baseByKey.get(section.key) ?? section
    return (
      <Bump trigger={base.items.length} className={className}>
        {mode === 'shop' ? shopSectionMeta(base, totals) : planSectionMeta(base)}
      </Bump>
    )
  }

  function planSectionMeta(section: Section): string {
    const money = estimateText({ ...planEstimate(section.items, rates), czkMinor: null }, currencies)
    return section.items.length === 0 ? 'zatím nic' : [itemsCount(section.items.length), money].filter(Boolean).join(' · ')
  }

  /** Cena polozky a pod ni stitek akce (stejny jako v sablone a v obchode). */
  function rowSub(it: TripItem) {
    const priceText = unitPrice(it.expectedPriceMinor, it.expectedCurrency, it.unit) ?? 'bez ceny'
    const label = dealLabel(it)
    if (!label) return priceText
    return (
      <span className="offer-sub">
        <span>{priceText}</span>
        <DealTag label={label} />
      </span>
    )
  }

  /** Prave tlacitko na polozce — zkratky k detailu (pocitac). */
  function itemMenu(it: TripItem) {
    return onContextMenu(() => {
      const actions: ContextAction[] = [{ label: 'Otevřít', icon: 'chevron_right', onSelect: () => setItemId(it.id) }]
      if (mode === 'shop') {
        actions.push(
          inBasket(it)
            ? { label: 'Vrátit mezi nekoupené', icon: 'refresh', onSelect: () => toggleFound(it) }
            : { label: 'Do košíku', icon: 'check', onSelect: () => toggleFound(it) },
        )
        if (it.status !== 'not_found' && !inBasket(it)) {
          actions.push({
            label: 'Nemají to',
            icon: 'x_circle',
            onSelect: () => sync.change(it.id, { status: 'not_found', actualPriceMinor: null }),
          })
        }
      } else {
        actions.push({ label: 'O kus víc', icon: 'plus', onSelect: () => setQty(it, it.qty + 1) })
      }
      for (const store of otherStores(route, it.storeId)) {
        actions.push({ label: `Přesunout ${intoStore(store.name)}`, icon: 'arrow_right', onSelect: () => moveTo(it, store) })
      }
      actions.push({ label: 'Odebrat z nákupu', icon: 'trash', tone: 'error', onSelect: () => removeItem(it) })
      return actions
    }, it.name)
  }

  function renderPlanRow(it: TripItem, sectionKey: string, index: number) {
    return (
      <SelectRow
        key={it.id}
        data-item-id={it.id}
        data-row-section={sectionKey}
        onContextMenu={itemMenu(it)}
        className={[`plan-row${drag?.itemId === it.id ? ' is-dragging' : ''}`, flashClass(it.id)].filter(Boolean).join(' ')}
      >
        <button
          type="button"
          className="grip"
          aria-label={
            desktop
              ? `Přesunout ${it.name}. Táhni, nebo použij šipky; doleva a doprava do vedlejšího obchodu.`
              : `Přesunout ${it.name}. Táhni, nebo použij šipky nahoru a dolů.`
          }
          onPointerDown={(e) => startDrag(e, it, sectionKey, index)}
          onKeyDown={(e) => onGripKey(e, it, sectionKey, index)}
        >
          <Icon name="grip" size={desktop ? 18 : 20} />
        </button>
        <button type="button" className="row-main" onClick={() => setItemId(it.id)}>
          <TextCol title={it.name} sub={rowSub(it)} />
        </button>
        <Stepper
          value={formatQty(it.qty, it.unit)}
          removes={it.qty <= 1}
          decrementLabel={it.qty <= 1 ? `Odebrat: ${it.name}` : `Méně: ${it.name}`}
          incrementLabel={`Více: ${it.name}`}
          onDecrement={() => setQty(it, it.qty - 1)}
          onIncrement={() => setQty(it, it.qty + 1)}
        />
      </SelectRow>
    )
  }

  function renderShopBody(section: Section) {
    return (
      <ShopSectionBody
        section={section}
        route={route}
        stores={stores}
        nameOf={s.nameOf}
        flashClass={flashClass}
        dealLabel={dealLabel}
        onToggle={toggleFound}
        onMove={moveTo}
        onOpen={(it) => setItemId(it.id)}
        onItemMenu={itemMenu}
      />
    )
  }

  const placingItem = placing.map((id) => trip.items.find((i) => i.id === id)).find((i) => i && !inBasket(i))
  const placeCard = placingItem && route.length > 1 && (
    <div className="place-card" role="group" aria-label={`Kam patří ${placingItem.name}`}>
      <div className="place-card-head">
        <span className="meta">{`Kam patří ${placingItem.name}?`}</span>
        <IconButton icon="x" label="Nechat, jak je" tone="muted" onClick={() => setPlacing([])} />
      </div>
      <ScrollRow className="chips-scroll">
        {route.map((store) => (
          <Chip
            key={store.id}
            active={placingItem.storeId === store.id}
            onClick={() => {
              if (placingItem.storeId !== store.id) moveTo(placingItem, store)
              setPlacing((prev) => prev.filter((id) => id !== placingItem.id))
            }}
          >
            {store.name}
          </Chip>
        ))}
      </ScrollRow>
    </div>
  )

  const titleChip = suggestedTitle && !trip.title?.trim() && !titleDismissed && (
    <div className="title-suggest">
      <Chip
        onClick={() => {
          setTitleDismissed(true)
          void sync.updateTrip({ title: suggestedTitle }).catch(() => undefined)
        }}
      >
        <Icon name="pencil" size={16} />
        {`Pojmenovat „${suggestedTitle}“`}
      </Chip>
      <IconButton icon="x" tone="muted" label="Nepojmenovávat" onClick={() => setTitleDismissed(true)} />
    </div>
  )

  const addBar = !done && (
    <AddBar
      trip={trip}
      route={route}
      catalog={catalogItems}
      templates={templates.res.state === 'ok' ? templates.res.data : []}
      frequent={frequent.res.state === 'ok' ? frequent.res.data : []}
      primary={mode === 'shop'}
      onAdd={addItems}
      onPickTemplate={() => navigate(`/nakup/${trip.id}/sablona`)}
      onBackToPlanning={mode === 'shop' && !desktop ? backToPlanning : undefined}
    />
  )

  const notices = (
    <>
      {done && <Paragraph>Nákup je dokončený. Opravy se pořád ukládají.</Paragraph>}
      {(error ?? sync.error) && <Paragraph tone="error">{error ?? sync.error}</Paragraph>}
      {placeCard}
      {dealsBanner}
      {titleChip}
      {trip.items.length === 0 && (
        <EmptyState title="Seznam je prázdný">Napiš, co potřebuješ koupit, nebo vyber ze šablony.</EmptyState>
      )}
    </>
  )

  const shop = shopTotals(sections, totals, rates)
  const finishLabel = done ? 'Zavřít' : 'Hotovo'
  const finishKind = done || remaining === 0 ? 'primary' : undefined
  const shopLabel = trip.status === 'planning' ? 'Nakupuju' : 'Zpět k nakupování'
  const canShop = trip.items.length > 0

  const sheets = (
    <>
      <ItemSheet
        item={item}
        mode={mode}
        store={item ? storeOfItem(item, stores) : null}
        moveTargets={item ? otherStores(route, item.storeId) : []}
        onClose={() => {
          setItemId(null)
          setScanPrice(false)
        }}
        startWithPrice={scanPrice}
        onChange={sync.change}
        onRemove={removeItem}
        onSaveToTemplate={saveToTemplate}
        deals={item ? itemDeals(item) : null}
      />
      <TripMenuSheet
        open={menu}
        onClose={() => setMenu(false)}
        trip={trip}
        stores={stores}
        sync={sync}
        onBackToPlanning={mode === 'shop' && !done ? backToPlanning : undefined}
        onDeleted={() => navigate('/', { replace: true })}
      />
      <ConfirmSheet
        open={confirmFinish}
        title="Dokončit nákup?"
        confirmLabel="Dokončit i tak"
        onConfirm={finishNow}
        onClose={() => setConfirmFinish(false)}
      >
        {`Na seznamu ${remainingText(remaining)}. Nekoupené věci zůstanou v dokončeném nákupu.`}
      </ConfirmSheet>
      {dealsSheet}
      <BarcodeScanner
        open={scanning}
        onClose={() => setScanning(false)}
        onCode={scanned}
        hint="Položka se na seznamu sama najde"
      />
      <ScanLinkSheet
        code={unknownCode}
        items={trip.items}
        catalog={catalogItems}
        onClose={() => setUnknownCode(null)}
        onLinked={(updated, tripItemId) => {
          catalog.setData((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
          setUnknownCode(null)
          setScanPrice(true)
          setItemId(tripItemId)
        }}
      />
      {undoToast}
      {contextMenu}
    </>
  )

  const scanButton = (bordered: boolean) =>
    mode === 'shop' && trip.items.length > 0 ? (
      <IconButton icon="barcode" label="Naskenovat čárový kód" bordered={bordered} onClick={() => setScanning(true)} />
    ) : null

  if (desktop) {
    const names = [...new Set(trip.items.flatMap((i) => (i.createdBy ? [i.createdBy] : [])))].flatMap(
      (id) => s.nameOf(id) ?? [],
    )
    const currencyKeys = Object.keys(estimate.byCurrency)
    const onlyCzk = currencyKeys.every((c) => c === 'CZK')
    const bigEstimate =
      currencyKeys.length === 0
        ? null
        : !onlyCzk && estimate.czkMinor !== null
          ? `≈ ${formatCzkRounded(estimate.czkMinor)}`
          : estimateText({ ...estimate, czkMinor: null }, currencies)
    const routeChanged = routeDraft !== null && routeDraft.join() !== trip.storeIds.join()

    return (
      <div className="desk-view">
        <div className="desk-view-main">
          <DeskToolbar
            crumb={[name.date, mode === 'shop' ? (done ? 'Hotovo' : 'Nakupuje se') : 'Plánuje se', itemsCount(trip.items.length)]
              .filter(Boolean)
              .join(' · ')}
            title={name.title}
            actions={
              <>
                <SyncStatus pending={sync.pending} offline={sync.offline} />
                {scanButton(true)}
                {mode === 'shop' && !done && (
                  <Button icon="pencil" onClick={backToPlanning}>
                    Zpět k plánování
                  </Button>
                )}
                <IconButton icon="more" label="Nastavení nákupu" bordered onClick={() => setMenu(true)} />
                {mode === 'plan' && (
                  <Button kind="primary" disabled={!canShop} onClick={() => goShopping()}>
                    {shopLabel}
                  </Button>
                )}
              </>
            }
          />
          {addBar}
          {notices}
          <div ref={listRef} className="board">
            {sections
              .filter((section) => mode === 'plan' || section.items.length > 0)
              .map((section) => {
                const number = route.findIndex((store) => store.id === section.key) + 1
                return (
                  <Panel
                    key={section.key}
                    className={`board-col${drag && drag.sectionKey === section.key ? ' is-drop-target' : ''}`}
                    data-section-key={section.key}
                    aria-label={section.title}
                  >
                    <div className="board-col-head">
                      {number > 0 && route.length > 1 && <span className="num-badge">{number}</span>}
                      <span className="text-col">
                        <span className="board-col-title">{section.title}</span>
                        {sectionMetaBump(section, 'meta muted tabular')}
                      </span>
                    </div>
                    <Divider />
                    <div className="board-col-rows">
                      {mode === 'plan'
                        ? section.items.map((it, index) => renderPlanRow(it, section.key, index))
                        : renderShopBody(section)}
                    </div>
                  </Panel>
                )
              })}
          </div>
        </div>

        {mode === 'plan' ? (
          <Inspector label="Souhrn nákupu">
            <div className="stack-4">
              <Label as="span">Odhad</Label>
              <span className="inspector-big">{bigEstimate ?? 'Zatím bez cen'}</span>
            </div>
            {currencyKeys.length > 0 && route.length > 1 && (
              <div className="stack-4">
                {baseSections
                  .filter((section) => section.store !== null && section.items.length > 0)
                  .map((section) => (
                    <MoneyLine
                      key={section.key}
                      label={section.title}
                      value={estimateText({ ...planEstimate(section.items, rates), czkMinor: null }, currencies) ?? 'bez cen'}
                    />
                  ))}
              </div>
            )}
            {estimate.withoutPrice > 0 && (
              <Paragraph size="meta">{`${itemsCount(estimate.withoutPrice)} zatím bez ceny`}</Paragraph>
            )}
            <Divider />
            <div className="stack-8">
              <Label as="span">{route.length > 1 ? 'Trasa' : 'Obchod'}</Label>
              <RouteEditor stores={stores} selected={routeDraft ?? trip.storeIds} onChange={setRouteDraft} />
              {routeChanged && (
                <div className="button-row">
                  <Button compact onClick={() => setRouteDraft(null)}>
                    Zrušit
                  </Button>
                  <Button compact disabled={routeBusy} onClick={() => void saveRoute(routeDraft)}>
                    Uložit trasu
                  </Button>
                </div>
              )}
            </div>
            {names.length > 0 && (
              <>
                <Divider />
                <div className="stack-8">
                  <Label as="span">{names.length === 1 ? 'Doplňuje' : 'Doplňují'}</Label>
                  <div className="row">
                    <AvatarStack names={names} size={30} />
                    <span>{joinNames(names)}</span>
                  </div>
                </div>
              </>
            )}
          </Inspector>
        ) : (
          <Inspector label="Útrata">
            <div className="stack-4">
              <Label as="span">V košíku</Label>
              {shop.bigTotal && <span className="inspector-big">{shop.bigTotal}</span>}
            </div>
            {shop.withStore.length > 1 && (
              <div className="stack-4">
                {shop.withStore.map((section) => (
                  <MoneyLine
                    key={section.key}
                    label={section.title}
                    value={formatMoney(totals.byStore[section.key]?.minor ?? 0, section.store?.currency ?? 'CZK')}
                  />
                ))}
                {shop.czkLine && <MoneyLine label="Celkem" value={shop.czkLine} strong={false} />}
              </div>
            )}
            <Divider />
            <Paragraph tone="fg">
              {trip.items.length === 0
                ? 'Na seznamu zatím nic není.'
                : remaining > 0
                  ? `Na seznamu ${remainingText(remaining)}.`
                  : 'Všechno je vyřešené.'}
            </Paragraph>
            <Button kind={finishKind} full onClick={finish}>
              {finishLabel}
            </Button>
          </Inspector>
        )}
        {sheets}
      </div>
    )
  }

  const subtitle = [
    name.date,
    route.length > 1
      ? route.map((store) => storeShortName(store.name)).join(' → ')
      : trip.storeIds.length === 1 && trip.title?.trim()
        ? route[0]?.name
        : null,
    itemsCount(trip.items.length),
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="app-shell">
      <div className="page">
        <ScreenHeader
          title={name.title}
          subtitle={subtitle}
          onBack={() => navigate('/')}
          right={
            <>
              {(sync.pending > 0 || sync.offline) && <SyncStatus pending={sync.pending} offline={sync.offline} />}
              {scanButton(false)}
              <IconButton icon="more" label="Nastavení nákupu" onClick={() => setMenu(true)} />
            </>
          }
        />
        <main className="page-content stack-8">
          {addBar}
          {notices}
          {trip.items.length > 0 && mode === 'plan' && (
            // Jeden plochy seznam hlavicek a radku: pri tahu do jineho obchodu
            // React radek jen presune mezi sourozenci, neprestavuje ho.
            <div ref={listRef} className="stack-8">
              {sections.flatMap((section) => [
                withHeads ? (
                  <div key={`hlavicka:${section.key}`} className="plan-section-head" data-section-key={section.key}>
                    <SectionHead title={section.title} meta={sectionMetaBump(section)} />
                  </div>
                ) : (
                  <span key={`hlavicka:${section.key}`} data-section-key={section.key} hidden />
                ),
                ...section.items.map((it, index) => renderPlanRow(it, section.key, index)),
              ])}
            </div>
          )}
          {mode === 'shop' &&
            sections.map((section) =>
              section.items.length === 0 ? null : (
                <section key={section.key} className="stack-8">
                  {withHeads && <SectionHead title={section.title} meta={shopSectionMeta(section, totals)} />}
                  {renderShopBody(section)}
                </section>
              ),
            )}
        </main>
        <Footer>
          {mode === 'plan' ? (
            <>
              <div className="footer-sum">
                <span className="meta muted">Odhad</span>
                <span className="footer-sum-value tabular">{estimateText(estimate, currencies) ?? 'zatím bez cen'}</span>
              </div>
              <Button kind="primary" full disabled={!canShop} onClick={() => goShopping()}>
                {shopLabel}
              </Button>
            </>
          ) : (
            <div className="shop-footer">
              <div className="text-col">
                {shop.bigTotal && <span className="shop-total tabular">{shop.bigTotal}</span>}
                <span className="meta muted">
                  {[remaining > 0 ? remainingText(remaining) : 'vše vyřešeno', shop.footerMeta].filter(Boolean).join(' · ')}
                </span>
              </div>
              <Button kind={finishKind} onClick={finish}>
                {finishLabel}
              </Button>
            </div>
          )}
        </Footer>
      </div>
      {sheets}
    </div>
  )
}

